import React, { useState, useEffect } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import './Game.css';
import Card from './Card';
import DrinkPopup from './DrinkPopup';

function Game({ socket, lobby, lobbyCode, playerName, toggleFullscreen, isFullscreen }) {
  const [playerMultiplier, setPlayerMultiplier] = useState(1);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState('');
  const [myCards, setMyCards] = useState([]);
  const [pyramid, setPyramid] = useState([]);
  const [busRider, setBusRider] = useState('');
  const busRiderRef = React.useRef('');
  const [busCards, setBusCards] = useState([]);
  const [flippingCards, setFlippingCards] = useState(new Set());
  const [busPosition, setBusPosition] = useState(0);
  const [busRound, setBusRound] = useState(1);
  const [busRevealedCards, setBusRevealedCards] = useState([false, false, false, false]);
  const [notification, setNotification] = useState('');
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [revealedCards, setRevealedCards] = useState([false, false, false, false]);
  const [showDrinkPopup, setShowDrinkPopup] = useState(false);
  const [drinkPopupMaxDrinks, setDrinkPopupMaxDrinks] = useState(5);
  const [showMatchResultPopup, setShowMatchResultPopup] = useState(false);
  const [matchResultData, setMatchResultData] = useState(null);
  const [showMustDrinkPopup, setShowMustDrinkPopup] = useState(false);
  const [mustDrinkAmount, setMustDrinkAmount] = useState(1);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [busWaitingForDrink, setBusWaitingForDrink] = useState(false);
  const [pyramidMatchDecision, setPyramidMatchDecision] = useState(null);
  const [playerCardCounts, setPlayerCardCounts] = useState([]);
  const [matchTimer, setMatchTimer] = useState(0);
  const matchTimerIntervalRef = React.useRef(null);

  // Initialize cards from lobby when component mounts or lobby updates
  useEffect(() => {
    if (lobby && lobby.players && lobby.gameState === 'playing') {
      const myPlayer = lobby.players.find(p => p.name === playerName);
      console.log('Game.js useEffect - Looking for player:', playerName);
      console.log('Game.js useEffect - All players:', lobby.players.map(p => p.name));
      console.log('Game.js useEffect - Found player:', myPlayer);
      console.log('Game.js useEffect - Player cards:', myPlayer?.cards);
      if (myPlayer && myPlayer.cards && myPlayer.cards.length > 0) {
        console.log('Game.js useEffect - Setting', myPlayer.cards.length, 'cards');
        setMyCards(myPlayer.cards);
      }
      if (myPlayer && myPlayer.multiplier) {
        setPlayerMultiplier(myPlayer.multiplier);
      }
    }
  }, [lobby, playerName]);

  useEffect(() => {
    if (!socket) return;

    socket.on('phaseUpdate', ({ phase, question, currentPlayer }) => {
      console.log('phaseUpdate received:', { phase, question, currentPlayer, playerName, isMyTurn: currentPlayer === playerName });
      setCurrentPhase(phase);
      setCurrentPlayer(currentPlayer);
      setIsMyTurn(currentPlayer === playerName);
      setHasGuessed(false);
      showNotification(`${question} - ${currentPlayer}'s turn`);
    });

    socket.on('guessResult', ({ correct, card, player, cardIndex, canDistribute, mustDrink, maxDrinks }) => {
      const result = correct ? '✓ Correct!' : '✗ Wrong!';
      
      // Update lobby state to reflect revealed card for ALL players
      if (lobby && lobby.players) {
        const playerObj = lobby.players.find(p => p.name === player);
        if (playerObj && playerObj.revealedCards) {
          playerObj.revealedCards[cardIndex] = true;
          playerObj.cards[cardIndex] = card;
        }
      }
      
      if (player === playerName) {
        setRevealedCards(prev => {
          const newRevealed = [...prev];
          newRevealed[cardIndex] = true;
          return newRevealed;
        });

        // Wrong guess - player must drink
        if (mustDrink) {
          showNotification(`${result} - You must drink! 🍺`);
          const myPlayer = lobby?.players.find(p => p.name === playerName);
          const multiplier = myPlayer?.multiplier || 1;
          setMustDrinkAmount(1 * multiplier);
          setTimeout(() => setShowMustDrinkPopup(true), 1000);
        }
        // Correct guess - player can distribute drinks
        else if (canDistribute) {
          showNotification(`${result} - Give out drinks!`);
          console.log('maxDrinks from server:', maxDrinks);
          const drinks = maxDrinks !== undefined ? maxDrinks : 5;
          console.log('Setting drinkPopupMaxDrinks to:', drinks);
          setDrinkPopupMaxDrinks(drinks);
          setTimeout(() => setShowDrinkPopup(true), 1000);
        } else {
          showNotification(result);
        }
      } else {
        if (mustDrink) {
          showNotification(`${player}: ${result} - Drinks! 🍺`);
        } else if (canDistribute) {
          showNotification(`${player}: ${result} - Gives out drinks!`);
        } else {
          showNotification(`${player}: ${result}`);
        }
      }
    });

    socket.on('nextPlayer', ({ currentPlayer }) => {
      console.log('nextPlayer received:', { currentPlayer, playerName, isMyTurn: currentPlayer === playerName });
      setCurrentPlayer(currentPlayer);
      setIsMyTurn(currentPlayer === playerName);
      setHasGuessed(false);
      showNotification(`${currentPlayer}'s turn`);
    });

    socket.on('gameStarted', (updatedLobby) => {
      console.log('Game.js - gameStarted event received');
      console.log('Game.js - gameStarted - All players:', updatedLobby.players.map(p => ({ name: p.name, cardCount: p.cards?.length || 0 })));
      const myPlayer = updatedLobby.players.find(p => p.name === playerName);
      console.log('Game.js - gameStarted - Looking for:', playerName);
      console.log('Game.js - gameStarted - Found player:', myPlayer?.name);
      console.log('Game.js - gameStarted - Player has cards:', myPlayer?.cards?.length || 0);
      if (myPlayer && myPlayer.cards && myPlayer.cards.length > 0) {
        console.log('Game.js - gameStarted - Setting', myPlayer.cards.length, 'cards');
        setMyCards([...myPlayer.cards]); // Force new array reference
        setRevealedCards([...(myPlayer.revealedCards || [false, false, false, false])]);
      } else {
        console.error('Game.js - gameStarted - FAILED to set cards for', playerName);
      }
    });

    socket.on('pyramidPhase', ({ pyramid, playerCardCounts: counts }) => {
      setCurrentPhase('pyramid');
      setPyramid(pyramid);
      if (counts) setPlayerCardCounts(counts);
      showNotification('Pyramid Phase! Match your cards!');
    });

    socket.on('playerCardCounts', ({ playerCardCounts: counts }) => {
      if (counts) setPlayerCardCounts(counts);
    });

    socket.on('pyramidCardRevealed', ({ row, col, card }) => {
      const cardKey = `${row}-${col}`;
      setFlippingCards(prev => new Set(prev).add(cardKey));
      
      setTimeout(() => {
        setPyramid(prev => {
          const newPyramid = [...prev];
          newPyramid[row][col] = { ...newPyramid[row][col], card, revealed: true };
          return newPyramid;
        });
        setFlippingCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(cardKey);
          return newSet;
        });
      }, 300);
    });

    socket.on('drinkReceived', ({ targetPlayer, amount }) => {
      if (targetPlayer === playerName) {
        setMustDrinkAmount(amount * playerMultiplier);
        setTimeout(() => setShowMustDrinkPopup(true), 500);
      }
    });

    socket.on('pyramidMatchDecision', ({ row, col, card, eligiblePlayers, drinks, timeLimit }) => {
      const canMatch = eligiblePlayers.includes(playerName);
      setPyramidMatchDecision({ row, col, card, drinks, canMatch, timeLimit });
      setMatchTimer(timeLimit / 1000);
    });

    socket.on('pyramidTimerUpdate', ({ timeLeft }) => {
      setMatchTimer(timeLeft);
      if (timeLeft <= 0) {
        setPyramidMatchDecision(null);
        setMatchTimer(0);
      }
    });

    socket.on('pyramidNoCardMatching', ({ row, col, card }) => {
      showNotification(`No Card Matching - ${card.value}`);
    });

    socket.on('pyramidNoMatch', ({ row, col }) => {
      setPyramidMatchDecision(null);
      setMatchTimer(0);
      showNotification('No match - continuing...');
    });

    socket.on('pyramidWaitingForDistribution', ({ playerName: distributingPlayer }) => {
      showNotification(`Waiting for ${distributingPlayer} to choose who to give drinks...`);
    });

    socket.on('pyramidWaitingForDrink', ({ playerName: drinkingPlayer }) => {
      showNotification(`Waiting for ${drinkingPlayer} to drink...`);
    });

    socket.on('pyramidMatchResult', ({ row, col, card, matchingPlayer, drinks, multiplier, canDistribute }) => {
      setPyramidMatchDecision(null);
      setMatchTimer(0);
      
      if (matchingPlayer === playerName) {
        // I matched - I get to give out drinks!
        showNotification(`You matched! Give out ${drinks * multiplier} sips!`);
        setDrinkPopupMaxDrinks(drinks);
        setTimeout(() => setShowDrinkPopup(true), 1000);
        
        // Remove ONE matching card from my hand
        setMyCards(prev => {
          const newCards = [...prev];
          const matchIndex = newCards.findIndex(c => c.value === card.value);
          if (matchIndex !== -1) {
            newCards.splice(matchIndex, 1);
          }
          return newCards;
        });
      } else {
        showNotification(`${matchingPlayer} matched and gives out ${drinks * multiplier} sips!`);
      }
    });

    socket.on('pyramidCardRemoved', ({ row, col }) => {
      setPyramid(prev => {
        const newPyramid = [...prev];
        if (newPyramid[row] && newPyramid[row][col]) {
          newPyramid[row][col] = { ...newPyramid[row][col], removed: true };
        }
        return newPyramid;
      });
    });

    socket.on('cardMatched', ({ player, row, col, drinks, cardValue }) => {
      showNotification(`${player} matched ${cardValue}! Give ${drinks} drink(s)!`);
      
      // Mark card as matched and schedule removal
      setPyramid(prev => {
        const newPyramid = [...prev];
        newPyramid[row][col] = { ...newPyramid[row][col], matched: true };
        return newPyramid;
      });

      // Remove matched card after animation
      setTimeout(() => {
        setPyramid(prev => {
          const newPyramid = [...prev];
          newPyramid[row][col] = { ...newPyramid[row][col], removed: true };
          return newPyramid;
        });
      }, 1000);
      
      if (player === playerName) {
        setDrinkPopupMaxDrinks(drinks);
        setTimeout(() => setShowDrinkPopup(true), 1500);
      }
    });

    socket.on('busPhase', ({ rider, busCards: cards, busRound: round }) => {
      setBusRider(rider);
      busRiderRef.current = rider;
      setCurrentPhase('bus');
      setBusCards(cards || []);
      setBusRound(round || 1);
      setBusRevealedCards([false, false, false, false]);
      setIsMyTurn(rider === playerName);
      showNotification(`${rider} is riding the bus!`);
    });

    socket.on('busGuessResult', ({ correct, card, cardIndex, nextRound, restart, newCards, won, lost, cardsRemaining, busOnlyFail, drinkAmount, showDrinkPopup }) => {
      const currentBusRider = busRiderRef.current;
      // Reveal the card
      if (cardIndex !== undefined) {
        setBusRevealedCards(prev => {
          const newRevealed = [...prev];
          newRevealed[cardIndex] = true;
          return newRevealed;
        });
      }

      if (won) {
        showNotification(`✓ Correct! ${currentBusRider} WON THE BUS!`);
      } else if (lost) {
        showNotification(`✗ Wrong! ${currentBusRider} LOST - Deck ran out!`);
        if (currentBusRider === playerName && drinkAmount) {
          setMustDrinkAmount(drinkAmount);
          setTimeout(() => setShowMustDrinkPopup(true), 500);
        }
      } else if (busOnlyFail) {
        showNotification(`✗ Wrong! ${currentBusRider} drinks ${drinkAmount} sips! Next player's turn...`);
        if (currentBusRider === playerName && drinkAmount) {
          setMustDrinkAmount(drinkAmount);
          setTimeout(() => setShowMustDrinkPopup(true), 500);
        }
      } else if (restart) {
        showNotification(`Restarting with new cards... (${cardsRemaining} cards left)`);
        setBusWaitingForDrink(false);
        setTimeout(() => {
          setBusCards(newCards);
          setBusRound(1);
          setBusRevealedCards([false, false, false, false]);
        }, 500);
      } else if (correct && nextRound) {
        showNotification(`✓ Correct! Moving to round ${nextRound}`);
        setBusRound(nextRound);
      } else if (!correct && drinkAmount && currentBusRider === playerName) {
        // Wrong guess for bus rider - show drink popup
        showNotification(`✗ Wrong guess! Drink ${drinkAmount} sips!`);
        setMustDrinkAmount(drinkAmount);
        setBusWaitingForDrink(true);
        setTimeout(() => setShowMustDrinkPopup(true), 500);
      } else if (!correct) {
        showNotification(`✗ Wrong guess!`);
      }
    });

    socket.on('busCardRevealed', ({ position, card, drinkValue, isFaceCard }) => {
      if (isFaceCard) {
        showNotification(`✗ ${card.value}! Drink ${drinkValue} and start over!`);
      } else {
        showNotification(`✓ ${card.value} - Safe!`);
      }
      setBusCards(prev => {
        const newCards = [...prev];
        newCards[position] = { card, revealed: true };
        return newCards;
      });
    });

    socket.on('busReset', ({ drinkValue }) => {
      showNotification(`Starting over! Drink ${drinkValue}!`);
      setBusPosition(0);
      setBusCards([]);
    });

    socket.on('gameComplete', ({ winner, loser, message }) => {
      setCurrentPhase('complete');
      // Track game end
      if (typeof window.trackGameEnd === 'function') {
        window.trackGameEnd(lobbyCode);
      }
      if (message) {
        showNotification(message);
      } else if (winner) {
        showNotification(`Game Over! ${winner} wins!`);
      } else if (loser) {
        showNotification(`Game Over! ${loser} lost!`);
      }
    });

    return () => {
      socket.off('phaseUpdate');
      socket.off('guessResult');
      socket.off('nextPlayer');
      socket.off('pyramidPhase');
      socket.off('playerCardCounts');
      socket.off('pyramidCardRevealed');
      socket.off('cardMatched');
      socket.off('busPhase');
      socket.off('busCardRevealed');
      socket.off('busReset');
      socket.off('gameComplete');
      socket.off('gameStarted');
    };
  }, [socket, playerName]);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const handleGuess = (guess) => {
    console.log('handleGuess called:', { guess, isMyTurn, hasGuessed, lobbyCode });
    if (!isMyTurn || hasGuessed) {
      console.log('handleGuess blocked:', { isMyTurn, hasGuessed });
      return;
    }
    setHasGuessed(true);
    console.log('Emitting makeGuess:', { code: lobbyCode, guess });
    socket.emit('makeGuess', { code: lobbyCode, guess });
  };

  const handlePyramidReveal = (row, col) => {
    const isHost = lobby && socket && lobby.host === socket.id;
    console.log('handlePyramidReveal called:', { row, col, isHost, hasLobby: !!lobby, hasSocket: !!socket });
    if (!isHost) {
      console.log('Not host, cannot reveal');
      return;
    }
    console.log('Emitting revealPyramidCard event');
    socket.emit('revealPyramidCard', { code: lobbyCode, row, col });
  };

  // Removed handleMatchCard - matching is now automatic

  const handleDistributeDrink = (targetPlayer, amount) => {
    socket.emit('distributeDrink', { code: lobbyCode, targetPlayer, amount });
    showNotification(`You gave ${amount} drink(s) to ${targetPlayer}!`);
  };

  const handlePyramidMatchChoice = (wantsToMatch) => {
    socket.emit('pyramidMatchChoice', { code: lobbyCode, wantsToMatch });
    setPyramidMatchDecision(null);
    setMatchTimer(0);
  };

  const handleMatchResultClose = () => {
    // Immediately remove matched card
    if (matchResultData && matchResultData.hasMatch) {
      socket.emit('removeMatchedCard', { code: lobbyCode, row: matchResultData.row, col: matchResultData.col });
    }
    setShowMatchResultPopup(false);
    setMatchResultData(null);
  };

  const handleRevealBusCard = (position) => {
    if (!isMyTurn) return;
    socket.emit('revealBusCard', { code: lobbyCode, position });
  };

  const handleBusGuess = (guess) => {
    if (busRider !== playerName) return;
    socket.emit('busGuess', { code: lobbyCode, guess });
  };

  const handlePyramidComplete = () => {
    socket.emit('pyramidComplete', lobbyCode);
  };

  // Calculate total revealed pyramid cards
  const getTotalRevealedCards = () => {
    if (!pyramid || pyramid.length === 0) return 0;
    let count = 0;
    pyramid.forEach(row => {
      row.forEach(card => {
        if (card.revealed) count++;
      });
    });
    return count;
  };

  const allPyramidCardsRevealed = getTotalRevealedCards() === 15;

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="game-info">
          <h2 className="phase-title">
            {currentPhase === 'round1' && 'Round 1: Red or Black?'}
            {currentPhase === 'round2' && 'Round 2: Higher or Lower?'}
            {currentPhase === 'round3' && 'Round 3: Inside or Outside?'}
            {currentPhase === 'round4' && 'Round 4: Guess the Suit'}
            {currentPhase === 'pyramid' && 'Pyramid Phase'}
            {currentPhase === 'bus' && 'Riding the Bus'}
            {currentPhase === 'complete' && 'Game Complete!'}
          </h2>
          {currentPlayer && currentPhase !== 'pyramid' && currentPhase !== 'complete' && (
            <p className="current-player">
              {isMyTurn ? "It's your turn!" : `${currentPlayer}'s turn`}
            </p>
          )}
        </div>
        <button onClick={toggleFullscreen} className="fullscreen-btn">
          {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
        </button>
      </div>

      {notification && (
        <div className="notification">
          {notification}
        </div>
      )}

      <div className="game-content">
        {(currentPhase === 'round1' || currentPhase === 'round2' || 
          currentPhase === 'round3' || currentPhase === 'round4') && (
          <div className="round-phase">
            <div className="my-cards">
              <h3>Your Cards</h3>
              <div className="cards-display">
                {myCards.map((card, index) => (
                  <div key={index} className={`card-wrapper ${revealedCards[index] ? 'revealed' : ''}`}>
                    <Card 
                      card={card} 
                      faceDown={!revealedCards[index]}
                    />
                  </div>
                ))}
              </div>
            </div>

            {lobby && lobby.players && currentPlayer && currentPlayer !== playerName && (
              <div className="other-players-cards">
                {(() => {
                  const activePlayer = lobby.players.find(p => p.name === currentPlayer);
                  if (!activePlayer) return null;
                  return (
                    <div className="player-cards-section">
                      <h4>{activePlayer.name}'s Cards</h4>
                      <div className="cards-display-small">
                        {activePlayer.cards && activePlayer.cards.map((card, index) => (
                          <div key={index} className="card-wrapper-small">
                            <Card 
                              card={card} 
                              faceDown={!activePlayer.revealedCards || !activePlayer.revealedCards[index]}
                              small
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {(() => {
              console.log('Rendering guess buttons check:', { isMyTurn, hasGuessed, currentPhase, currentPlayer, playerName });
              return isMyTurn && !hasGuessed;
            })() && (
              <div className="guess-buttons">
                {currentPhase === 'round1' && (
                  <>
                    <button onClick={() => handleGuess('red')} className="guess-btn red">
                      Red
                    </button>
                    <button onClick={() => handleGuess('black')} className="guess-btn black">
                      Black
                    </button>
                  </>
                )}
                {currentPhase === 'round2' && (
                  <>
                    <button onClick={() => handleGuess('higher')} className="guess-btn higher">
                      Higher
                    </button>
                    <button onClick={() => handleGuess('lower')} className="guess-btn lower">
                      Lower
                    </button>
                  </>
                )}
                {currentPhase === 'round3' && (
                  <>
                    <button onClick={() => handleGuess('inside')} className="guess-btn inside">
                      Inside
                    </button>
                    <button onClick={() => handleGuess('outside')} className="guess-btn outside">
                      Outside
                    </button>
                    <button onClick={() => handleGuess('same')} className="guess-btn same">
                      Same
                    </button>
                  </>
                )}
                {currentPhase === 'round4' && (
                  <>
                    <button onClick={() => handleGuess('hearts')} className="guess-btn hearts">
                      ♥ Hearts
                    </button>
                    <button onClick={() => handleGuess('diamonds')} className="guess-btn diamonds">
                      ♦ Diamonds
                    </button>
                    <button onClick={() => handleGuess('clubs')} className="guess-btn clubs">
                      ♣ Clubs
                    </button>
                    <button onClick={() => handleGuess('spades')} className="guess-btn spades">
                      ♠ Spades
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {currentPhase === 'pyramid' && (
          <div className="pyramid-phase">
            <p className="pyramid-turn-info">Host turns the card</p>
            <div className="pyramid-container">
              {pyramid.map((row, rowIndex) => {
                const visibleCards = row.filter(card => !card.removed);
                if (visibleCards.length === 0) return null;
                
                return (
                  <div key={rowIndex} className="pyramid-row">
                    {row.map((pyramidCard, colIndex) => {
                      if (pyramidCard.removed) return null;
                      const cardKey = `${rowIndex}-${colIndex}`;
                      const isFlipping = flippingCards.has(cardKey);
                      return (
                        <div
                          key={colIndex}
                          className={`pyramid-card ${pyramidCard.revealed ? 'revealed' : ''} ${pyramidCard.matched ? 'matched' : ''} ${isFlipping ? 'flipping' : ''}`}
                          onClick={() => !pyramidCard.revealed && handlePyramidReveal(rowIndex, colIndex)}
                        >
                          {pyramidCard.revealed ? (
                            <Card card={pyramidCard.card} small className={isFlipping ? 'flipping' : ''} />
                          ) : (
                            <Card card={{ suit: 'hearts', value: 'A' }} small faceDown />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="player-card-counts">
              <h3>Players</h3>
              <div className="player-counts-list">
                {playerCardCounts.map((p, i) => (
                  <div key={i} className={`player-count-item ${p.name === playerName ? 'is-me' : ''} ${p.cardCount === 0 ? 'no-cards' : ''}`}>
                    <span className="player-count-name">{p.name}</span>
                    <span className="player-count-badge">{p.cardCount} {p.cardCount === 1 ? 'card' : 'cards'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="my-cards">
              <h3>Your Remaining Cards</h3>
              <div className="cards-display">
                {myCards.map((card, index) => (
                  <Card 
                    key={index} 
                    card={card} 
                    small
                  />
                ))}
              </div>
            </div>

            {lobby && socket && lobby.host === socket.id && (
              <button 
                onClick={handlePyramidComplete} 
                className="btn btn-complete"
                disabled={!allPyramidCardsRevealed}
              >
                Complete Pyramid Phase {allPyramidCardsRevealed ? '' : `(${getTotalRevealedCards()}/15)`}
              </button>
            )}
          </div>
        )}

        {currentPhase === 'bus' && (
          <div className="round-phase">
            <div className="my-cards">
              <h3>{busRider === playerName ? 'Your Cards' : `${busRider}'s Cards`}</h3>
              <div className="cards-display">
                {busCards.map((cardData, index) => (
                  <div key={index} className={`card-wrapper ${busRevealedCards[index] ? 'revealed' : ''}`}>
                    <Card 
                      card={cardData.card} 
                      faceDown={!busRevealedCards[index]}
                    />
                  </div>
                ))}
              </div>
            </div>

            {busRider === playerName && !busWaitingForDrink && (
              <div className="guess-buttons">
                {busRound === 1 && (
                  <>
                    <button onClick={() => handleBusGuess('red')} className="guess-btn red">
                      Red
                    </button>
                    <button onClick={() => handleBusGuess('black')} className="guess-btn black">
                      Black
                    </button>
                  </>
                )}
                {busRound === 2 && (
                  <>
                    <button onClick={() => handleBusGuess('higher')} className="guess-btn higher">
                      Higher
                    </button>
                    <button onClick={() => handleBusGuess('lower')} className="guess-btn lower">
                      Lower
                    </button>
                  </>
                )}
                {busRound === 3 && (
                  <>
                    <button onClick={() => handleBusGuess('inside')} className="guess-btn inside">
                      Inside
                    </button>
                    <button onClick={() => handleBusGuess('outside')} className="guess-btn outside">
                      Outside
                    </button>
                    <button onClick={() => handleBusGuess('same')} className="guess-btn same">
                      Same
                    </button>
                  </>
                )}
                {busRound === 4 && (
                  <>
                    <button onClick={() => handleBusGuess('hearts')} className="guess-btn hearts">
                      ♥ Hearts
                    </button>
                    <button onClick={() => handleBusGuess('diamonds')} className="guess-btn diamonds">
                      ♦ Diamonds
                    </button>
                    <button onClick={() => handleBusGuess('clubs')} className="guess-btn clubs">
                      ♣ Clubs
                    </button>
                    <button onClick={() => handleBusGuess('spades')} className="guess-btn spades">
                      ♠ Spades
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {currentPhase === 'complete' && (
          <div className="game-complete">
            <h2>🎉 Game Complete! 🎉</h2>
            <p>{notification || 'Thanks for playing!'}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => {
                socket.emit('backToLobby', lobbyCode);
                setCurrentPhase(null);
              }}
            >
              Back to Lobby
            </button>
          </div>
        )}
      </div>

      {showDrinkPopup && lobby && lobby.players && (
        <DrinkPopup
          players={lobby.players.filter(p => p.name !== playerName)}
          onClose={() => setShowDrinkPopup(false)}
          onDistribute={handleDistributeDrink}
          maxDrinks={drinkPopupMaxDrinks}
          multiplier={playerMultiplier}
          socket={socket}
          lobbyCode={lobbyCode}
          currentPhase={currentPhase}
        />
      )}

      {showMatchResultPopup && matchResultData && (
        <div className="match-result-popup-overlay">
          <div className="match-result-popup">
            <div className="match-result-header">
              <h2>🎴 Card Revealed</h2>
            </div>
            <div className="match-result-content">
              <div className="revealed-card-display">
                <Card card={matchResultData.card} />
              </div>
              {matchResultData.hasMatch ? (
                <div className="match-info">
                  <h3>✓ Match Found!</h3>
                  {matchResultData.matches.map((match, idx) => (
                    <p key={idx} className="match-player">
                      <strong>{match.player}</strong> drinks <strong>{match.drinks}</strong> {match.drinks > 1 ? 'times' : 'time'}! 🍺
                    </p>
                  ))}
                </div>
              ) : (
                <div className="no-match-info">
                  <h3>✗ No Card Matching</h3>
                  <p>No players have this card</p>
                </div>
              )}
              <button className="btn btn-primary" onClick={handleMatchResultClose}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {pyramidMatchDecision && (
        <div className="pyramid-match-overlay">
          <div className="pyramid-match-popup">
            <div className="pyramid-match-header">
              <h2>Card Revealed: {pyramidMatchDecision.card.value} of {pyramidMatchDecision.card.suit}</h2>
              <div className="match-timer">{matchTimer.toFixed(1)}s</div>
            </div>
            <div className="pyramid-match-content">
              {pyramidMatchDecision.canMatch ? (
                <>
                  <p className="match-question">You have this card! Do you want to match?</p>
                  <p className="match-reward">Give out <strong>{pyramidMatchDecision.drinks * playerMultiplier}</strong> sips!</p>
                  <div className="match-buttons">
                    <button className="btn btn-success" onClick={() => handlePyramidMatchChoice(true)}>
                      Yes, Match!
                    </button>
                    <button className="btn btn-secondary" onClick={() => handlePyramidMatchChoice(false)}>
                      No, Skip
                    </button>
                  </div>
                </>
              ) : (
                <p className="match-waiting">Waiting for players to decide...</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showMustDrinkPopup && (
        <div className="must-drink-popup-overlay">
          <div className="must-drink-popup">
            <div className="must-drink-header">
              <h2>🍺 You Must Drink!</h2>
            </div>
            <div className="must-drink-content">
              <p className="drink-message">Take <strong>{mustDrinkAmount}</strong> sip{mustDrinkAmount > 1 ? 's' : ''}!</p>
              <button className="btn btn-primary" onClick={() => {
                setShowMustDrinkPopup(false);
                socket.emit('drinkPopupDismissed', { code: lobbyCode, playerName });
                // If bus phase, notify server drink is done so it can deal new cards
                if (currentPhase === 'bus') {
                  socket.emit('busDrinkComplete', { code: lobbyCode });
                }
              }}>
                Done! 🍺
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Game;
